import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Search } from 'lucide-react'
import { format } from 'date-fns'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Pagination } from '@/shared/components/ui/pagination'
import { QueryErrorScreen } from '@/shared/components/ui/query-error-screen'
import { RefreshButton } from '@/shared/components/ui/refresh-button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { DEFAULT_PAGE_SIZE } from '@/shared/constants'
import { routes } from '@/shared/constants'
import { useMyBusinesses } from '@/features/businesses/hooks/useMyBusinesses'
import { TicketStatusChip } from './components/TicketStatusChip'
import { useOrgTicketStates } from './hooks/useOrgTicketCatalogs'
import { useOrgTickets } from './hooks/useOrgTickets'

const PAGE_SIZE = DEFAULT_PAGE_SIZE
const ALL = 'all'
// Tipos de soporte visibles en el escaque (decisión 6 del goal).
const TIPOS = ['incidencia', 'hallazgo', 'propuesta']

/**
 * Escaque "Tickets" (goal portal-comunidad-y-tickets-organizacion): el
 * propietario ve TODOS los tickets de soporte de su organización con su
 * estado. El alcance lo garantiza tickets-server (ownership contra Zeus).
 */
export function TicketsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [page, setPage] = useState(1)

  const state = params.get('state') ?? ALL
  const type = params.get('type') ?? ALL
  const businessId = params.get('business') ?? ALL
  const q = params.get('q') ?? ''

  const { tickets, totalItems, loading, error, refetch } = useOrgTickets({
    page,
    perPage: PAGE_SIZE,
    state: state === ALL ? null : state,
    type: type === ALL ? null : type,
    businessId: businessId === ALL ? null : businessId,
    q,
  })
  const { states } = useOrgTicketStates()
  const { businesses } = useMyBusinesses({ take: 100, skip: 0 })

  const updateParam = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params)
    if (!value || value === ALL) next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
    setPage(1)
  }

  if (error && tickets.length === 0) {
    return (
      <QueryErrorScreen
        error={error}
        onRetry={() => refetch()}
        title={t('common.error')}
      />
    )
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-[36px] font-normal leading-[1.1] tracking-tight text-ink">
            {t('tickets.title')}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">{t('tickets.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <RefreshButton
            onClick={async () => {
              await refetch()
            }}
          />
          <Button onClick={() => navigate(routes.app.tickets.new)}>
            <Plus className="mr-1.5 size-4" />
            {t('tickets.newTicket')}
          </Button>
        </div>
      </header>

      <Card>
        <div className="mb-4 grid gap-3 md:grid-cols-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-muted" />
            <Input
              className="pl-9"
              placeholder={t('tickets.searchPlaceholder')}
              value={q}
              onChange={(e) => updateParam('q', e.target.value)}
            />
          </div>
          <Select value={state} onValueChange={(v) => updateParam('state', v)}>
            <SelectTrigger aria-label={t('tickets.columns.state')}>
              <SelectValue placeholder={t('tickets.allStates')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('tickets.allStates')}</SelectItem>
              {states.map((s) => (
                <SelectItem key={s.code} value={s.code}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={(v) => updateParam('type', v)}>
            <SelectTrigger aria-label={t('tickets.columns.type')}>
              <SelectValue placeholder={t('tickets.allTypes')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('tickets.allTypes')}</SelectItem>
              {TIPOS.map((tp) => (
                <SelectItem key={tp} value={tp}>
                  {tp}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={businessId}
            onValueChange={(v) => updateParam('business', v)}
          >
            <SelectTrigger aria-label={t('tickets.columns.business')}>
              <SelectValue placeholder={t('tickets.allBusinesses')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('tickets.allBusinesses')}</SelectItem>
              {businesses.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.legalName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading && tickets.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-muted">
            {t('common.loading')}
          </p>
        ) : tickets.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-muted">
            {t('tickets.empty')}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-ink-muted">
                    <th className="py-2 pr-4">{t('tickets.columns.code')}</th>
                    <th className="py-2 pr-4">{t('tickets.columns.title')}</th>
                    <th className="py-2 pr-4">{t('tickets.columns.state')}</th>
                    <th className="py-2 pr-4">{t('tickets.columns.type')}</th>
                    <th className="py-2 pr-4">
                      {t('tickets.columns.business')}
                    </th>
                    <th className="py-2">{t('tickets.columns.date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr key={ticket.code} className="border-b last:border-0">
                      <td className="py-3 pr-4 font-mono text-xs">
                        <Link
                          to={routes.app.tickets.detail(ticket.code)}
                          className="text-primary hover:underline"
                        >
                          {ticket.code}
                        </Link>
                      </td>
                      <td className="py-3 pr-4">
                        <Link
                          to={routes.app.tickets.detail(ticket.code)}
                          className="font-medium hover:underline"
                        >
                          {ticket.title}
                        </Link>
                        {ticket.fixConfirmed && (
                          <Badge variant="active" className="ml-2">
                            {t('tickets.detail.fixConfirmed')}
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <TicketStatusChip status={ticket.status} />
                      </td>
                      <td className="py-3 pr-4 capitalize">{ticket.type}</td>
                      <td className="py-3 pr-4">{ticket.businessName ?? '—'}</td>
                      <td className="py-3 whitespace-nowrap">
                        {format(new Date(ticket.createdAt), 'dd/MM/yyyy')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              totalItems={totalItems}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>
    </div>
  )
}
